import { PollTextAnswerCard, PollTextAnswerContainer, useUpdatePin } from '@lessonup/pin-renderer';
import { PollPinSettingsText } from '@lessonup/pins-shared';
import { Button, color, IconRemove, styled } from '@lessonup/ui-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TipTapInput } from '../../../../../utils/yjs/TipTapInput';

export interface PollPinTextAnswerAreaProps {
  pinId: string;
  pinSettings: PollPinSettingsText;
  isInteractive?: boolean;
}

export const PollPinTextAnswerArea = ({ pinId, pinSettings, isInteractive = false }: PollPinTextAnswerAreaProps) => {
  const { answers } = pinSettings;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('editor');

  const dispatch = useUpdatePin();

  const handleDeleteAnswer = (id: string) => {
    const updatedAnswers = answers.filter((answer) => answer.id !== id);
    dispatch({
      type: 'updatePinSettings',
      settings: { answers: updatedAnswers },
    });
  };

  useEffect(() => {
    if (!selectedId) return;

    // Handle clicking outside the button, so selectedId can be reset
    const handleClickOutside = (event: MouseEvent) => {
      if (selectedId && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSelectedId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedId]);

  return (
    <PollTextAnswerContainer containerRef={containerRef}>
      {answers.map((answer, index) => (
        <PollTextAnswerCard
          key={`poll-answer-${answer.id}`}
          index={index}
          answer={answer}
          data-testid={`answer-card-${answer.id}`}
          onClick={() => setSelectedId(answer.id)}
        >
          <AnswerInputWrapper>
            <TipTapInput
              pinId={pinId}
              pinComponentId="poll"
              fragmentKey={answer.id}
              placeholder={t('answerAreaPinComponent.textPoll.answerPlaceholder', {
                answerIndex: index + 1,
              })}
            />
          </AnswerInputWrapper>
          {isInteractive && answer.id === selectedId && (
            <DeleteButtonWrapper>
              <Button
                buttonType="negative"
                size="small"
                onClick={() => handleDeleteAnswer(answer.id)}
                iconStart={<IconRemove />}
                disabled={answers.length < 3}
                aria-label={t('delete')}
              />
            </DeleteButtonWrapper>
          )}
        </PollTextAnswerCard>
      ))}
    </PollTextAnswerContainer>
  );
};

const AnswerInputWrapper = styled.div`
  flex: 1;
  min-width: 0;

  &:hover:not(:focus-within) {
    outline: 1px solid ${color.neutral.outline.background};
  }

  &:focus-within {
    outline: 2px solid ${color.utils.focus.stroke};
  }
`;

const DeleteButtonWrapper = styled.div`
  button:disabled {
    cursor: not-allowed; // TODO This should be fixed in the @lessonup/ui-components level instead
  }
`;
